import { useState, useEffect } from 'react';
import { 
  Shield, 
  Smartphone, 
  Mail, 
  Key, 
  QrCode, 
  Copy, 
  CheckCircle,
  AlertTriangle,
  Fingerprint,
  Eye,
  EyeOff
} from 'lucide-react';
import { authenticationService } from '@/services/authenticationService';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/authStore';

interface TwoFactorSetupProps {
  onComplete?: () => void;
}

export function TwoFactorSetup({ onComplete }: TwoFactorSetupProps) {
  const { user } = useAuthStore();
  const { success, error } = useToast();
  const [step, setStep] = useState<'method' | 'setup' | 'verify' | 'backup' | 'biometric'>('method');
  const [selectedMethod, setSelectedMethod] = useState<'SMS' | 'EMAIL' | 'AUTHENTICATOR' | null>(null);
  const [setupData, setSetupData] = useState<any>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [biometricSupported, setBiometricSupported] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkBiometricSupport();
  }, []);

  const checkBiometricSupport = () => {
    const supported = !!(navigator.credentials && navigator.credentials.create);
    setBiometricSupported(supported);
  };

  const handleMethodSelection = async (method: 'SMS' | 'EMAIL' | 'AUTHENTICATOR') => {
    setSelectedMethod(method);
    setLoading(true);
    
    try {
      const data = await authenticationService.setupTwoFactor(user!.id, method);
      setSetupData(data);
      setStep('setup');
    } catch (err) {
      error('Failed to setup two-factor authentication');
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async () => {
    if (!verificationCode.trim()) {
      error('Please enter the verification code');
      return;
    }

    setLoading(true);
    try {
      const codes = await authenticationService.enableTwoFactor(user!.id, verificationCode);
      setBackupCodes(codes);
      setStep('backup');
      success('Two-factor authentication enabled successfully!');
    } catch (err) {
      error('Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricSetup = async () => {
    if (!biometricSupported) {
      error('Biometric authentication is not supported on this device');
      return;
    }

    setLoading(true);
    try {
      const { options } = await authenticationService.setupBiometric(user!.id, 'FINGERPRINT');
      
      // Create credential using WebAuthn API
      const credential = await navigator.credentials.create({
        publicKey: options
      });

      if (credential) {
        await authenticationService.registerBiometric(user!.id, 'FINGERPRINT', {
          id: credential.id,
          publicKey: 'mock_public_key' // In real app, extract from credential
        });
        
        success('Biometric authentication setup successfully!');
        onComplete?.();
      }
    } catch (err) {
      error('Failed to setup biometric authentication');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    success('Copied to clipboard');
  };

  const MethodSelectionStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Shield className="w-12 h-12 text-primary-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Secure Your Account
        </h2>
        <p className="text-gray-600">
          Choose your preferred two-factor authentication method
        </p>
      </div>

      <div className="space-y-4">
        <button
          onClick={() => handleMethodSelection('AUTHENTICATOR')}
          disabled={loading}
          className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
        >
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <Key className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Authenticator App</h3>
              <p className="text-sm text-gray-600">
                Use Google Authenticator, Authy, or similar apps
              </p>
              <span className="text-xs text-green-600 font-medium">Recommended</span>
            </div>
          </div>
        </button>

        <button
          onClick={() => handleMethodSelection('SMS')}
          disabled={loading}
          className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
        >
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <Smartphone className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">SMS Text Message</h3>
              <p className="text-sm text-gray-600">
                Receive codes via text message
              </p>
            </div>
          </div>
        </button>

        <button
          onClick={() => handleMethodSelection('EMAIL')}
          disabled={loading}
          className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors text-left"
        >
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
              <Mail className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900">Email</h3>
              <p className="text-sm text-gray-600">
                Receive codes via email
              </p>
            </div>
          </div>
        </button>
      </div>
    </div>
  );

  const SetupStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Setup {selectedMethod === 'AUTHENTICATOR' ? 'Authenticator App' : selectedMethod}
        </h2>
      </div>

      {selectedMethod === 'AUTHENTICATOR' && setupData && (
        <div className="space-y-6">
          <div className="bg-gray-50 rounded-lg p-6 text-center">
            <QrCode className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-sm text-gray-600 mb-4">
              Scan this QR code with your authenticator app
            </p>
            <div className="bg-white p-4 rounded-lg border-2 border-dashed border-gray-300">
              <div className="text-xs text-gray-500">QR Code would appear here</div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Manual Setup</h4>
            <p className="text-sm text-blue-700 mb-2">
              If you can't scan the QR code, enter this secret key manually:
            </p>
            <div className="flex items-center space-x-2">
              <code className="flex-1 px-3 py-2 bg-white border border-blue-300 rounded text-sm font-mono">
                {setupData.secret}
              </code>
              <button
                onClick={() => copyToClipboard(setupData.secret)}
                className="btn btn-outline btn-sm"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>

          <button
            onClick={() => setStep('verify')}
            className="w-full btn btn-primary"
          >
            Continue to Verification
          </button>
        </div>
      )}

      {(selectedMethod === 'SMS' || selectedMethod === 'EMAIL') && (
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-blue-600 mr-2" />
              <p className="text-sm text-blue-700">
                A verification code will be sent to your {selectedMethod.toLowerCase()} 
                when you need to authenticate.
              </p>
            </div>
          </div>

          <button
            onClick={() => setStep('verify')}
            className="w-full btn btn-primary"
          >
            Continue to Verification
          </button>
        </div>
      )}
    </div>
  );

  const VerificationStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Verify Your Setup
        </h2>
        <p className="text-gray-600">
          Enter the 6-digit code from your {selectedMethod === 'AUTHENTICATOR' ? 'authenticator app' : selectedMethod?.toLowerCase()}
        </p>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Verification Code
          </label>
          <input
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="w-full px-4 py-3 text-center text-2xl font-mono border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="000000"
            maxLength={6}
          />
        </div>

        <button
          onClick={handleVerification}
          disabled={loading || verificationCode.length !== 6}
          className="w-full btn btn-primary"
        >
          {loading ? 'Verifying...' : 'Verify & Enable'}
        </button>
      </div>
    </div>
  );

  const BackupCodesStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Two-Factor Authentication Enabled!
        </h2>
        <p className="text-gray-600">
          Save these backup codes in a safe place
        </p>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
          <div>
            <h4 className="font-medium text-yellow-800">Important!</h4>
            <p className="text-sm text-yellow-700 mt-1">
              These backup codes can be used to access your account if you lose access 
              to your primary 2FA method. Each code can only be used once.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-900">Backup Codes</h4>
          <button
            onClick={() => setShowBackupCodes(!showBackupCodes)}
            className="btn btn-outline btn-sm flex items-center"
          >
            {showBackupCodes ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {showBackupCodes ? 'Hide' : 'Show'}
          </button>
        </div>
        
        {showBackupCodes && (
          <div className="grid grid-cols-2 gap-2">
            {backupCodes.map((code, index) => (
              <div key={index} className="flex items-center space-x-2">
                <code className="flex-1 px-2 py-1 bg-white border border-gray-300 rounded text-sm font-mono">
                  {code}
                </code>
                <button
                  onClick={() => copyToClipboard(code)}
                  className="p-1 hover:bg-gray-200 rounded"
                >
                  <Copy className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        {biometricSupported && (
          <button
            onClick={() => setStep('biometric')}
            className="w-full btn btn-outline flex items-center justify-center"
          >
            <Fingerprint className="w-4 h-4 mr-2" />
            Setup Biometric Authentication
          </button>
        )}
        
        <button
          onClick={onComplete}
          className="w-full btn btn-primary"
        >
          Complete Setup
        </button>
      </div>
    </div>
  );

  const BiometricStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <Fingerprint className="w-12 h-12 text-primary-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Setup Biometric Authentication
        </h2>
        <p className="text-gray-600">
          Add fingerprint or face recognition for faster login
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">Benefits</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Faster and more convenient authentication</li>
          <li>• Enhanced security with biometric data</li>
          <li>• Works alongside your existing 2FA method</li>
        </ul>
      </div>

      <div className="space-y-3">
        <button
          onClick={handleBiometricSetup}
          disabled={loading}
          className="w-full btn btn-primary"
        >
          {loading ? 'Setting up...' : 'Setup Biometric Authentication'}
        </button>
        
        <button
          onClick={onComplete}
          className="w-full btn btn-outline"
        >
          Skip for Now
        </button>
      </div>
    </div>
  );

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        {step === 'method' && <MethodSelectionStep />}
        {step === 'setup' && <SetupStep />}
        {step === 'verify' && <VerificationStep />}
        {step === 'backup' && <BackupCodesStep />}
        {step === 'biometric' && <BiometricStep />}
      </div>
    </div>
  );
}